import { useState } from "react"
import useStore from "../../store"
import SelectDropdown from "./SelectDropdown"
import ReusableModal from "./ReusableModal"
import { Button, Field, Input } from "@chakra-ui/react"
import { DateTime } from "luxon"
import DatePicker from "react-datepicker"
import { colors } from "../../utilities/colors"
import ReusableDrawer from "./ReusableDrawer"
import { NeutralButton } from "./Buttons"
import Spinner from "./Spinner"
import { createListCollection } from "@chakra-ui/react"
import { CloseButton } from "@chakra-ui/react"
import {
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "../ui/select"

export default function UploadFileDrawer({ isOpen, onClose, dealerId }) {
  const setSuccessMessage = useStore((state) => state.setSuccessMessage)
  const setErrMessage = useStore((state) => state.setErrMessage)
  const createDealerFile = useStore((state) => state.createDealerFile)
  const fetchDealerDocuments = useStore((state) => state.fetchDealerDocuments)
  const fetchLotPhotos = useStore((state) => state.fetchLotPhotos)
  const dealerFolders = useStore((state) => state.dealerFolders)
  const people = useStore((state) => state.people)
  const setCreatingDealerFile = useStore((state) => state.setCreatingDealerFile)
  const creatingDealerFile = useStore((state) => state.creatingDealerFile)
  const todaysDate = DateTime.now().toFormat('MM/dd/yyyy')
  const updatePerson = useStore((state) => state.updatePerson)
  const user = useStore((state) => state.user)
  const [dlExpDate, setDlExpDate] = useState(null)
  const [licenseExpDate, setLicenseExpDate] = useState(null)
  const [file, setFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [selectedFileType, setSelectedFileType] = useState("")
  const [selectedPerson, setSelectedPerson] = useState(null)
  const updateDealer = useStore((state) => state.updateDealer)
  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    dfile: '',
    dealer_folder_id: ''
  })

  const fileOptions = createListCollection({
    items: [
      { label: "Lot Photo", value: "lotPhoto", folder: "Branch Files" },
      { label: "ID", value: "id", folder: "Legal Files" },
      { label: "Bank Statement", value: "bankStatement", folder: "Legal Files" },
      { label: "SOS Search", value: "sosSearch", folder: "Legal Files" },
      { label: "UCC Search", value: "uccSearch", folder: "Legal Files" },
      { label: "Secretary of State Filings", value: "secOfState", folder: "Legal Files" },
      { label: "Credit Report", value: "creditReport", folder: "Legal Files" },
      { label: "Account Receivable Details", value: "accountReceivableDetails", folder: "Legal Files" },
      { label: "Document", value: "document", folder: "Branch Files" },
      { label: "Dealer's License", value: "dealersLicense", folder: "Legal Files" }
    ]
  })

  const findFolderIdByDirectoryName = (folders, directoryName) => {
    const folder = folders.find(folder => folder.attributes['directory-name'] === directoryName)
    return folder ? folder.id : null
  }

  const handleFileTypeChange = (fileType) => {
    setSelectedFileType(fileType.items[0].label)
    setSelectedPerson(null)

    setFormValues({
      name: fileType.items[0].label,
      description: "",
      dfile: "",
      dealer_folder_id: findFolderIdByDirectoryName(dealerFolders, fileType.items[0].folder),
    })
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    setFile(file)

    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    if (file) {
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async () => {
    setCreatingDealerFile(true)
    const formData = new FormData()
    formData.append('dealer_file[dfile]', file)
    formData.append('dealer_file[dealer_folder_id]', formValues.dealer_folder_id)

    if(selectedPerson && selectedFileType === 'ID') {
      formData.append("dealer_file[name]", `${selectedPerson.label}'s Driver's License (${todaysDate})`)

      await updatePerson(dealerId, selectedPerson.value, {
        dl_exp_on: dlExpDate,
        app_dl_added_on: todaysDate,
        app_dl_by: user['userInitials']
      })
      fetchDealerDocuments(dealerId)
      setSelectedPerson(null)
    } else if (selectedPerson && selectedFileType === 'Credit Report') {
      formData.append("dealer_file[name]", `${selectedPerson.label}'s Credit Report (${todaysDate})`)
      setSelectedPerson(null)
    } else {
      formData.append('dealer_file[name]', formValues.name)
    }
    if(selectedFileType === "Dealer's License") {
      updateDealer(dealerId, {
        dealers_license_expires_on: licenseExpDate,
      })
    }

    try {
      const response = await createDealerFile(formData, dealerId)
      if (response.ok) {
        setSuccessMessage('File uploaded successfully!')
        fetchDealerDocuments(dealerId)
        fetchLotPhotos(dealerId)
      }
    } catch (error) {
      console.error("Error uploading file:", error)
      setErrMessage("Failed to upload file.")
    }
    setFormValues({
      name: "",
      description: "",
      dfile: "",
    })
    setCreatingDealerFile(false)
    setSelectedFileType(null)
    setFile(null)
    setImagePreview(false)
    onClose()
  }

  const handleRemoveFile = () => {
    setFile(null)
    setImagePreview(false)
  }

  const peopleList = createListCollection({
    items: people.map(person => ({ value: person.id, label: person.attributes['full-name'] }))
  })

  return(
    <ReusableDrawer
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom"
      bgColor={colors.deepOcean}
      title="Upload File"
      bodyStyle="flex flex-col px-4 space-y-4 overflow-visible"
      footerStyle="flex justify-center p-4"
      size="xl"
      bodyContent={
        <>
          <Field.Root>
            <SelectRoot
              collection={fileOptions}
              size="sm"
              positioning={{ sameWidth: true, placement: "bottom" }}
              onValueChange={(value) => handleFileTypeChange(value)}
            >
              <SelectTrigger
                borderWidth={2}
                borderColor={colors.grey}
                _focus={{ borderColor: colors.grey300 }}
                rounded="md"
                p={2}
              >
                <SelectValueText placeholder="Select File Type" />
              </SelectTrigger>
              <SelectContent portalled={false} width="full" >
                {fileOptions.items.map((item) => (
                  <SelectItem item={item} key={item.value} >
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
            {selectedFileType === "Dealer's License" &&
              <>
                <Field.Label fontSize={'sm'} color={'white'} htmlFor="datePicker" aria-labelledby="datePicker">
                  License Expires On
                </Field.Label>
                <DatePicker
                  id="datePicker"
                  selected={licenseExpDate}
                  onChange={setLicenseExpDate}
                  ariaLabel="Dealer's License Expires On"
                  className="px-1 w-full bg-grey-900 border-2 border-grey-700 focus:border-grey-300 focus:ring-0 rounded"
                />
              </>
            }
            {(selectedFileType === 'ID' || selectedFileType === 'Credit Report') &&
              <Field.Root mb={4}>
                <SelectRoot
                  collection={peopleList}
                  size="sm"
                  positioning={{ sameWidth: true, placement: "bottom" }}
                  onValueChange={(value) => setSelectedPerson(value.items[0])}
                >
                  <SelectTrigger
                    borderWidth={2}
                    borderColor={colors.grey}
                    _focus={{ borderColor: colors.grey300 }}
                    rounded="md"
                    p={2}
                  >
                    <SelectValueText placeholder="Select Person" />
                  </SelectTrigger>
                  <SelectContent portalled={false} width="full" >
                    {peopleList.items.map((item) => (
                      <SelectItem item={item} key={item.value} >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>

                {selectedFileType === 'ID' &&
                  <>
                    <Field.Label fontSize={'sm'} color={'white'} htmlFor="datePicker" aria-labelledby="datePicker">
                      ID Expires On
                    </Field.Label>
                    <DatePicker
                      id="datePicker"
                      selected={dlExpDate}
                      onChange={setDlExpDate}
                      ariaLabel="ID Expires On"
                      className="px-1 w-full bg-grey-900 border-2 border-grey-700 focus:border-grey-300 focus:ring-0 rounded"
                    />
                  </>
                }
              </Field.Root>
            }
          </Field.Root>
          <div className="flex items-center gap-1">
            <label className="relative bg-gray-300 rounded-md cursor-pointer">
              <span className="py-2 px-4 inline-block rounded border-2 border-grey-100">Choose File</span>
              <input
                type="file"
                className="sr-only"
                onChange={handleFileUpload}
                accept=".jpg, .jpeg, .png, .HEIC, .pdf"
              />
            </label>
            {file &&
              <CloseButton
                onClick={handleRemoveFile}
                size="md"
                data-testid="remove-file"
                color={colors.grey100}
              />
            }
          </div>

          {file && (
            <div className="relative w-24 h-24">
              <img src={imagePreview} alt="Preview" className="w-full h-full object-cover border border-gray-300 rounded" />
            </div>
          )}
        </>
      }
      footerContent={
        <>
          <NeutralButton
            text={"Upload File"}
            onClick={handleSubmit}
            style="mx-4"
            dataTestId="submit-upload-file"
            loading={creatingDealerFile}
            disabled={!selectedFileType || !file}
          />
          <NeutralButton
            text={"Cancel"}
            onClick={() => {
              onClose()
              setSelectedFileType(null)
              setFile(null)
              setImagePreview(false)
              setDlExpDate(null)
              setLicenseExpDate(null)
            }}
            style="mx-4"
            dataTestId="close"
          />
        </>
      }
    />
  )
}